import { Alert, Button, Link, Stack, TextField } from "@mui/material";
import { useState, type FormEvent } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import { api, errorMessage } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { AuthLayout } from "./AuthLayout";

export function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sso, setSso] = useState<{ available: boolean; required: boolean; name: string | null }>({ available: false, required: false, name: null });
  const from = (location.state as { from?: string } | null)?.from ?? "/app";

  async function checkSso(value: string) {
    if (!value.includes("@")) { setSso({ available: false, required: false, name: null }); return; }
    const { data } = await api.GET("/api/v1/accounts/auth/sso/check/", { params: { query: { email: value } } });
    if (data) setSso({ available: !!data.sso_available, required: !!data.sso_required, name: (data.name as string) ?? null });
  }
  const ssoUrl = `/api/v1/accounts/auth/sso/start/?email=${encodeURIComponent(email)}&next=${encodeURIComponent(from)}`;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mfaToken) {
        await verifyMfa(mfaToken, code);
        navigate(from, { replace: true });
        return;
      }
      const result = await login(email, password);
      if (result.mfaRequired) setMfaToken(result.mfaToken);
      else navigate(from, { replace: true });
    } catch (err) {
      const e2 = err as { error?: { code?: string } };
      if (e2?.error?.code === "sso_required") { window.location.assign(ssoUrl); return; }
      setError(e2?.error?.code === "account_locked" ? "Too many failed attempts. Try again in 15 minutes." : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={mfaToken ? "Two-factor authentication" : "Sign in"}
      subtitle={mfaToken ? "Enter the 6-digit code from your authenticator app, or a recovery code." : undefined}
      footer={<>New to CloudForge? <Link component={RouterLink} to="/signup">Start a free trial</Link></>}
    >
      <form onSubmit={onSubmit}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {mfaToken ? (
            <TextField label="Authentication code" value={code} onChange={(e) => setCode(e.target.value)} autoFocus
              inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }} required />
          ) : (
            <>
              <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={(e) => void checkSso(e.target.value)} required autoFocus autoComplete="email" />
              {sso.available && (
                <Button variant={sso.required ? "contained" : "outlined"} size="large" href={ssoUrl}>Continue with {sso.name ?? "single sign-on"}</Button>
              )}
              {!sso.required && <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />}
              <Link component={RouterLink} to="/forgot-password" variant="body2" alignSelf="flex-end">Forgot password?</Link>
            </>
          )}
          {(mfaToken || !sso.required) && <Button type="submit" variant="contained" disabled={busy} size="large">{mfaToken ? "Verify" : "Sign in"}</Button>}
          {mfaToken && <Button onClick={() => { setMfaToken(null); setCode(""); }}>Back</Button>}
        </Stack>
      </form>
    </AuthLayout>
  );
}
